import { resolveEffectiveHttpVersion } from '@terra/shared-utils';
import { MatchReplaceProcessor } from '../electron/match-replace-processor';
import type {
  HttpRequest as ProcessorHttpRequest,
  HttpResponse as ProcessorHttpResponse,
} from '../electron/proxy-types';
import type {
  MatchReplaceRule,
  MatchReplaceRuleStats,
} from '../store/proxy-store';
import type { HttpRequest } from '../types/http-request';
import {
  formatHttpRequestRaw,
  formatHttpResponseRaw,
} from './formatHttpForPanel';
import { matchesMatchReplaceRuleConditions } from './match-replace-conditions';

export type MatchReplacePreviewResult = {
  matches: boolean;
  result?: string;
  error?: string;
};

const previewProcessor = new MatchReplaceProcessor();

export function enrichHttpRequestForMatchReplaceMatching(
  request: HttpRequest,
  resolvedBodies?: { requestBody?: string; responseBody?: string },
): HttpRequest {
  return {
    ...request,
    body:
      request.body || resolvedBodies?.requestBody || request.bodyPreview || '',
    responseBody:
      request.responseBody ||
      resolvedBodies?.responseBody ||
      request.responseBodyPreview ||
      '',
  };
}

function toProcessorRequest(request: HttpRequest): ProcessorHttpRequest {
  return {
    id: request.id,
    sequentialId: request.sequentialId,
    method: request.method,
    url: request.url,
    host: request.host,
    headers: request.headers,
    rawHeaders: request.rawHeaders,
    body: request.body,
    timestamp:
      request.timestamp instanceof Date
        ? request.timestamp.getTime()
        : new Date(request.timestamp).getTime(),
    source: request.source,
    httpVersion: request.httpVersion,
    bodyRef: request.bodyRef,
    bodySize: request.bodySize,
    bodyStorageType: request.bodyStorageType,
    bodyContentType: request.bodyContentType,
    bodyPreview: request.bodyPreview,
    multipartParts: request.multipartParts,
    wasIntercepted: request.wasIntercepted,
    wasProxyModified: request.wasProxyModified,
    originalRequest: request.originalRequest,
  };
}

function toProcessorResponse(
  request: HttpRequest,
): ProcessorHttpResponse | null {
  if (!request.status) {
    return null;
  }

  return {
    id: `${request.id}-response`,
    requestId: request.id,
    statusCode: request.status,
    statusMessage: request.responseStatusMessage ?? '',
    headers: request.responseHeaders ?? {},
    rawHeaders: request.responseRawHeaders,
    body: request.responseBody ?? '',
    httpVersion: request.responseHttpVersion ?? request.httpVersion ?? '1.1',
    timestamp:
      request.timestamp instanceof Date
        ? request.timestamp.getTime()
        : new Date(request.timestamp).getTime(),
    duration: 0,
    bodyRef: request.responseBodyRef,
    bodyStorageType: request.responseBodyStorageType,
    bodyContentType: request.responseBodyContentType,
    bodyPreview: request.responseBodyPreview,
  };
}

function formatProcessorRequest(request: ProcessorHttpRequest): string {
  return formatHttpRequestRaw({
    method: request.method,
    url: request.url,
    headers: request.headers,
    rawHeaders: request.rawHeaders,
    body: request.body || undefined,
    httpVersion: resolveEffectiveHttpVersion({
      httpVersion: request.httpVersion,
      headers: request.rawHeaders ?? request.headers,
    }),
  });
}

function formatProcessorResponse(response: ProcessorHttpResponse): string {
  return formatHttpResponseRaw({
    status: response.statusCode,
    statusMessage: response.statusMessage,
    headers: response.headers,
    rawHeaders: response.rawHeaders,
    body: response.body || undefined,
    httpVersion: resolveEffectiveHttpVersion({
      httpVersion: response.httpVersion,
      headers: response.rawHeaders ?? response.headers,
    }),
  });
}

export function applyMatchReplaceRulePreview({
  rule,
  request,
}: {
  rule: MatchReplaceRule;
  request: HttpRequest;
}): MatchReplacePreviewResult {
  const enrichedRequest = enrichHttpRequestForMatchReplaceMatching(request);

  if (!rule.enabled) {
    return { matches: false };
  }

  if (!rule.matchPattern.trim() && rule.matchType !== 'addHeader') {
    return { matches: false };
  }

  if (!matchesMatchReplaceRuleConditions({ rule, request: enrichedRequest })) {
    return { matches: false };
  }

  try {
    const processorRequest = toProcessorRequest(enrichedRequest);
    const processorResponse = toProcessorResponse(enrichedRequest);
    const appliesToRequest =
      rule.matchLocation === 'request' || rule.matchLocation === 'both';
    const appliesToResponse =
      rule.matchLocation === 'response' || rule.matchLocation === 'both';

    let requestMatched = false;
    let responseMatched = false;
    let transformedRequest = processorRequest;
    let transformedResponse = processorResponse;

    if (appliesToRequest) {
      const afterRequest = previewProcessor.applyToRequest(processorRequest, [
        rule,
      ]);
      requestMatched = previewProcessor
        .getLastMatchedRuleIds()
        .includes(rule.id);
      if (requestMatched) {
        transformedRequest = afterRequest;
      }
    }

    if (appliesToResponse && processorResponse) {
      const afterResponse = previewProcessor.applyToResponse(
        processorResponse,
        processorRequest,
        [rule],
      );
      responseMatched = previewProcessor
        .getLastMatchedRuleIds()
        .includes(rule.id);
      if (responseMatched) {
        transformedResponse = afterResponse;
      }
    }

    if (!requestMatched && !responseMatched) {
      return { matches: false };
    }

    if (rule.matchLocation === 'response' && transformedResponse) {
      return {
        matches: true,
        result: formatProcessorResponse(transformedResponse),
      };
    }

    if (rule.matchLocation === 'both' && transformedResponse) {
      const requestPart = formatProcessorRequest(transformedRequest);
      const responsePart = formatProcessorResponse(transformedResponse);
      return {
        matches: true,
        result: `${requestPart}\n\n---\n\n${responsePart}`,
      };
    }

    return {
      matches: true,
      result: formatProcessorRequest(transformedRequest),
    };
  } catch (error) {
    return {
      matches: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export function countRuleMatchesInHistory({
  rule,
  requests,
}: {
  rule: MatchReplaceRule;
  requests: HttpRequest[];
}): number {
  if (!rule.enabled) {
    return 0;
  }

  return requests.reduce((count, request) => {
    const result = applyMatchReplaceRulePreview({
      rule,
      request: enrichHttpRequestForMatchReplaceMatching(request),
    });
    return count + (result.matches ? 1 : 0);
  }, 0);
}

export function buildRuleMatchCounts({
  rules,
  stats,
  requests,
}: {
  rules: MatchReplaceRule[];
  stats: Record<string, MatchReplaceRuleStats>;
  requests: HttpRequest[];
}): Record<string, number> {
  const counts: Record<string, number> = {};

  for (const rule of rules) {
    const persisted = stats[rule.id];
    counts[rule.id] =
      persisted != null
        ? persisted.matchCount
        : countRuleMatchesInHistory({ rule, requests });
  }

  return counts;
}
